import { useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  LmsOverdueService,
  LmsOverdueUploadError,
  type LmsOverdueUploadResult,
} from "@/integrations/api/services/lmsOverdueService";
import { toast } from "sonner";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function labelForKey(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[\s-]+/g, "_");
}

function isPlainNumber(value: unknown): boolean {
  if (typeof value === "number" && Number.isFinite(value)) return true;
  return typeof value === "string" && /^-?\d+(\.\d+)?$/.test(value.trim());
}

function isIssueKey(key: string): boolean {
  return /skip|error|fail|reject|invalid|unresolved|duplicate/.test(normalizeKey(key));
}

const IMPORTANT_STATS = new Set([
  "rows_loaded",
  "total_rows_in_file",
  "loans_matched",
  "loans_updated",
  "raw_pos_overdue_updated",
  "inserted_count",
  "skipped_row_count",
  "rows_updated",
  "updated_count",
]);

const STAT_ORDER = [
  "rows_loaded",
  "total_rows_in_file",
  "loans_matched",
  "raw_pos_overdue_updated",
  "loans_updated",
  "inserted_count",
  "skipped_row_count",
];

const COLUMN_ORDER = ["row", "row_number", "line", "loan_id", "applicant_id", "reason", "error", "message", "field"];

type StatItem = { key: string; label: string; value: string; warn: boolean };
type NoteItem = { key: string; label: string; value: string };
type TableSection = { key: string; title: string; columns: string[]; rows: Record<string, string>[]; total: number };
type ListSection = { key: string; title: string; items: string[]; total: number };

function formatStat(value: unknown): string {
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric.toLocaleString("en-IN") : String(value);
}

function cellText(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function orderedColumns(rows: Record<string, string>[]): string[] {
  const present = new Set<string>();
  rows.forEach((row) => Object.keys(row).forEach((key) => present.add(key)));
  const preferred = COLUMN_ORDER.filter((key) => present.has(key));
  const rest = [...present].filter((key) => !COLUMN_ORDER.includes(key));
  return [...preferred, ...rest];
}

function recordsFromArray(value: unknown[]): Record<string, string>[] | null {
  if (!value.every((item) => item && typeof item === "object" && !Array.isArray(item))) return null;
  return value.map((item) => {
    const record: Record<string, string> = {};
    Object.entries(item as Record<string, unknown>).forEach(([key, cell]) => {
      record[key] = cellText(cell);
    });
    return record;
  });
}

function splitResult(result: LmsOverdueUploadResult) {
  const stats: StatItem[] = [];
  const notes: NoteItem[] = [];
  const tables: TableSection[] = [];
  const lists: ListSection[] = [];

  Object.entries(result).forEach(([key, value]) => {
    if (value == null || value === "" || key === "detail") return;
    const label = labelForKey(key);
    const issue = isIssueKey(key);

    if (Array.isArray(value)) {
      const records = recordsFromArray(value);
      if (records) {
        tables.push({
          key,
          title: label,
          columns: orderedColumns(records),
          rows: records,
          total: records.length,
        });
        return;
      }
      lists.push({
        key,
        title: label,
        items: value.map((item) => cellText(item)),
        total: value.length,
      });
      return;
    }

    if (value && typeof value === "object") {
      const entries = Object.entries(value as Record<string, unknown>);
      if (issue && entries.length > 0 && entries.every(([, item]) => Array.isArray(item))) {
        entries.forEach(([group, rows]) => {
          const numbers = (rows as unknown[]).map((item) => cellText(item));
          lists.push({
            key: `${key}.${group}`,
            title: `${label}: ${labelForKey(group)}`,
            items: numbers,
            total: numbers.length,
          });
        });
        return;
      }
      return;
    }

    if (isPlainNumber(value)) {
      const normalized = normalizeKey(key);
      const warn = issue && Number(value) > 0;
      if (IMPORTANT_STATS.has(normalized) || warn) {
        stats.push({ key, label, value: formatStat(value), warn });
      }
      return;
    }

    if (normalizeKey(key) === "allocation_month") {
      notes.push({ key, label, value: String(value) });
    }
  });

  stats.sort((a, b) => {
    const aIndex = STAT_ORDER.indexOf(normalizeKey(a.key));
    const bIndex = STAT_ORDER.indexOf(normalizeKey(b.key));
    return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex);
  });

  const issueTables = tables.filter((section) => isIssueKey(section.key) && section.total > 0);
  const issueLists = lists.filter((section) => isIssueKey(section.key) && section.total > 0);

  const listedProblems =
    issueTables.reduce((sum, section) => sum + section.total, 0) +
    issueLists.reduce((sum, section) => sum + section.total, 0);
  const countedProblems = stats
    .filter((item) => item.warn)
    .reduce((sum, item) => sum + Number(item.value.replace(/,/g, "") || 0), 0);
  const problemCount = listedProblems > 0 ? listedProblems : countedProblems;

  return { stats, notes, issueTables, issueLists, problemCount };
}

function displayBody(body: unknown): LmsOverdueUploadResult | null {
  if (!body || typeof body !== "object" || Array.isArray(body)) return null;
  const record = { ...(body as LmsOverdueUploadResult) };
  const detail = record.detail;
  if (Array.isArray(detail)) {
    record.errors = detail.map((item) => {
      if (typeof item === "string") return { message: item };
      if (!item || typeof item !== "object") return { message: String(item) };
      const row = item as { loc?: unknown; msg?: unknown; type?: unknown };
      return {
        field: Array.isArray(row.loc) ? row.loc.map(String).join(".") : "",
        message: row.msg != null ? String(row.msg) : "",
        type: row.type != null ? String(row.type) : "",
      };
    });
    delete record.detail;
  } else if (detail && typeof detail === "object") {
    Object.entries(detail as Record<string, unknown>).forEach(([key, value]) => {
      if (record[key] == null) record[key] = value;
    });
    delete record.detail;
  }
  return record;
}

const LmsOverdueUpload = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<LmsOverdueUploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const selectFile = (next: File | null) => {
    if (!next) return;
    if (!LmsOverdueService.isAcceptedFile(next)) {
      setError("Upload an Excel or CSV file (.xlsx, .xls, or .csv).");
      setFile(null);
      return;
    }
    setError(null);
    setFailed(false);
    setResult(null);
    setFile(next);
  };

  const handleUpload = async () => {
    if (!file || uploading) return;
    setUploading(true);
    setError(null);
    setFailed(false);
    setResult(null);
    try {
      const data = await LmsOverdueService.uploadLmsOverdueAmount(file);
      setResult(data);
      const problems = splitResult(data).problemCount;
      if (problems > 0) {
        toast.warning(`Uploaded with ${problems.toLocaleString("en-IN")} skipped or failed rows`);
      } else {
        toast.success("LMS overdue amounts uploaded");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed";
      const body = err instanceof LmsOverdueUploadError ? displayBody(err.body) : null;
      if (body) setResult(body);
      setFailed(true);
      setError(message);
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const summary = result ? splitResult(result) : null;

  return (
    <div className="space-y-3">
      {!result && (
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          selectFile(event.dataTransfer.files?.[0] ?? null);
        }}
        className={`flex w-full flex-col items-center justify-center rounded-xl border border-dashed px-4 py-7 text-center transition-colors ${
          dragOver
            ? "border-[#22B55E] bg-[#f3fbf6]"
            : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100"
        }`}
      >
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm">
          <Upload className="h-4 w-4 text-gray-700" />
        </span>
        <span className="mt-2 text-sm font-medium text-gray-900">
          Drop the LMS file here, or click to browse
        </span>
        <span className="mt-1 text-xs text-gray-500">Excel or CSV, up to 20 MB</span>
      </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
        className="hidden"
        onChange={(event) => {
          selectFile(event.target.files?.[0] ?? null);
          event.target.value = "";
        }}
      />

      {file && (
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
          <FileSpreadsheet className="h-5 w-5 shrink-0 text-[#1ea34f]" />
          <div className="min-w-0 flex-1 text-left">
            <p className="truncate text-sm font-medium text-gray-900">{file.name}</p>
            <p className="text-xs text-gray-500">{formatBytes(file.size)}</p>
          </div>
          <button
            type="button"
            className="rounded-md p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-900"
            aria-label="Remove file"
            onClick={() => {
              setFile(null);
              setResult(null);
              setError(null);
              setFailed(false);
            }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {error && !summary && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      {!result && (
        <div className="flex justify-end">
          <Button type="button" size="sm" disabled={!file || uploading} onClick={handleUpload}>
            {uploading ? "Uploading..." : "Upload and update loans"}
          </Button>
        </div>
      )}

      {summary && (
        <div className="space-y-4">
          <div
            className={`flex items-start gap-2 rounded-lg border px-4 py-3 ${
              failed
                ? "border-red-200 bg-red-50"
                : summary.problemCount > 0
                  ? "border-amber-200 bg-amber-50"
                  : "border-[#b7ebc9] bg-[#f3fbf6]"
            }`}
          >
            {failed || summary.problemCount > 0 ? (
              <AlertTriangle className={`mt-0.5 h-5 w-5 shrink-0 ${failed ? "text-red-600" : "text-amber-600"}`} />
            ) : (
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#1ea34f]" />
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900">
                {failed ? "Upload failed" : summary.problemCount > 0 ? "Uploaded with skipped rows" : "Loan details updated"}
              </p>
              <p className="mt-0.5 text-sm text-gray-600">
                {failed
                  ? error
                  : summary.problemCount > 0
                    ? `${summary.problemCount.toLocaleString("en-IN")} skipped or failed rows are listed below.`
                    : "Overdue EMI and charges were applied to the matching loans."}
              </p>
            </div>
          </div>

          {summary.stats.length > 0 && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {summary.stats.map((item) => (
                <div
                  key={item.key}
                  className={`rounded-lg border px-3 py-3 ${
                    item.warn ? "border-amber-200 bg-amber-50" : "border-gray-200 bg-white"
                  }`}
                >
                  <p className="text-xs font-medium text-gray-500">{item.label}</p>
                  <p className={`mt-1 text-2xl font-semibold tabular-nums ${item.warn ? "text-amber-800" : "text-gray-900"}`}>
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          )}

          {summary.notes.length > 0 && (
            <p className="text-sm text-gray-600">
              {summary.notes.map((item) => `${item.label}: ${item.value}`).join(" · ")}
            </p>
          )}

          {summary.issueTables.map((section) => (
            <section key={section.key}>
              <div className="mb-2 flex items-center justify-between gap-3">
                <h3 className={`text-sm font-semibold ${/error|fail|reject/.test(section.key) ? "text-red-700" : "text-gray-900"}`}>
                  {section.title}
                </h3>
                <span className="text-xs font-medium text-gray-500">
                  {section.total.toLocaleString("en-IN")} {section.total === 1 ? "row" : "rows"}
                </span>
              </div>
              <div className="max-h-80 overflow-auto rounded-lg border border-gray-200">
                  <table className="w-full min-w-[32rem] border-collapse text-left text-sm">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr>
                        {section.columns.map((column) => (
                          <th key={column} className="whitespace-nowrap border-b px-3 py-2 text-xs font-semibold text-gray-600">
                            {labelForKey(column)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.rows.map((row, index) => (
                        <tr key={`${section.key}-${index}`} className="odd:bg-white even:bg-gray-50">
                          {section.columns.map((column) => (
                            <td key={column} className="break-words px-3 py-2 align-top text-gray-800">
                              {row[column] || "—"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
              </div>
            </section>
          ))}

          {summary.issueLists.map((section) => (
            <section key={section.key}>
              <div className="mb-2 flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-gray-900">{section.title}</h3>
                <span className="text-xs font-medium text-gray-500">
                  {section.total.toLocaleString("en-IN")}
                </span>
              </div>
              <div className="max-h-48 overflow-auto rounded-lg border border-gray-200 bg-white p-3">
                <p className="break-words text-sm leading-6 text-gray-800">{section.items.join(", ")}</p>
              </div>
            </section>
          ))}

          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
            onClick={() => {
              setFile(null);
              setResult(null);
              setError(null);
              setFailed(false);
            }}
            >
              Upload another file
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LmsOverdueUpload;
